import React, { useState } from 'react'

import Button from 'components_new/atoms/Button'
import TextField from 'components_new/atoms/TextField'

import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { useCustomFields } from 'redux/hooks/CustomFields'
import { useDashboardActions } from 'redux/actions/Dashboards'
import { useCustomFieldActions } from 'redux/actions/CustomFields'
import { useTranslation } from 'language/translator'
import { TDashboard } from 'types/GlobalDashboard'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

interface TagsProps {
  dashboard: TDashboard
}

const Tags = (props: TagsProps) => {
  const { dashboard } = props
  const { t } = useTranslation()
  const { tryPatchCustomFieldValue } = useDashboardActions()
  const { tryCreateCustomField } = useCustomFieldActions()
  const customFields = useCustomFields()

  const [newTagOpen, setNewTagOpen] = useState(false)
  const [newTag, setNewTag] = useState('')

  const tags = customFields
    .map((field) => {
      const value = dashboard.custom_field_values.find(
        (f) => f.custom_field_id === field.id
      )?.value

      return {
        id: field.id,
        field_name: field.field_name,
        value: value || null
      }
    })
    .sort((a, b) => sortAlphabeticalAsc(a.field_name, b.field_name))

  const duplicateTag = tags.some((field) => field.field_name === newTag)

  const handleSaveTag = (customFieldId: string, value: string | null) => {
    tryPatchCustomFieldValue(dashboard.id, {
      custom_field_id: customFieldId,
      value
    })
  }

  const handleAddTag = () => {
    tryCreateCustomField(newTag)

    setNewTagOpen(false)
    setNewTag('')
  }

  return (
    <>
      <SettingsGroup
        helperText={t('dashboards', 'tags_helper_text')}
        title={t('global', 'tags')}
      >
        {tags.map((tag) => (
          <TagItem
            key={tag.id}
            customFieldId={tag.id}
            fieldName={tag.field_name}
            value={tag.value}
            onSave={handleSaveTag}
          />
        ))}
        <SettingsItem
          onClick={() => setNewTagOpen(true)}
          title={t('dashboards', 'add_tag')}
          variant="add"
        />
      </SettingsGroup>

      <SimpleDialog
        actions={
          <>
            <Button variant="text" onClick={() => setNewTagOpen(false)}>
              {t('global', 'cancel')}
            </Button>
            <Button
              disabled={newTag.length === 0 || duplicateTag}
              variant="contained"
              onClick={handleAddTag}
            >
              {t('global', 'add')}
            </Button>
          </>
        }
        onClose={() => {
          setNewTag('')
          setNewTagOpen(false)
        }}
        open={newTagOpen}
        title={t('global', 'set_name')}
      >
        <TextField
          error={duplicateTag}
          helperText={
            duplicateTag ? t('dashboards', 'duplicate_tag') : undefined
          }
          fullWidth={true}
          label={t('global', 'name')}
          autoComplete="off"
          value={newTag}
          onChange={(event) => setNewTag(event.target.value)}
          sx={{ mt: 1, mb: 2 }}
          required
        />
      </SimpleDialog>
    </>
  )
}

interface TagItemProps {
  customFieldId: string
  fieldName: string
  value: string | null
  onSave: (customFieldId: string, value: string | null) => void
}

const TagItem = (props: TagItemProps) => {
  const { customFieldId, fieldName, value, onSave } = props
  const { t } = useTranslation()

  const [editOpen, setEditOpen] = useState(false)
  const [editValue, setEditValue] = useState(value || '')

  return (
    <>
      <SettingsItem
        onClick={() => {
          setEditValue(value || '')
          setEditOpen(true)
        }}
        title={fieldName}
        value={value || t('global', 'nothing_selected_yet')}
        variant="edit"
      />
      <SimpleDialog
        actions={
          <>
            <Button variant="text" onClick={() => setEditOpen(false)}>
              {t('global', 'cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                onSave(customFieldId, editValue || null)
                setEditOpen(false)
              }}
            >
              {t('global', 'save')}
            </Button>
          </>
        }
        onClose={() => setEditOpen(false)}
        open={editOpen}
        title={fieldName}
      >
        <TextField
          fullWidth={true}
          label={t('global', 'value')}
          autoComplete="off"
          value={editValue}
          onChange={(event) => setEditValue(event.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
      </SimpleDialog>
    </>
  )
}

export default Tags
